// SMS messaging (FE-30): pure types, parsing, gating, segment counting and copy. No React here;
// rendering is in MessagingPage.tsx and its parts, state in controller.ts.
//
// Seam: the wire types live in lib/api.ts (narrow local copies of the SMS contract) until the
// backend lane adds them to packages/contracts. Everything from the server is re-validated here:
// unknown enum values never render as a known state, malformed rows are dropped and counted, and
// free text (carrier reasons) is bounded and redacted before display.
import type {
  ConsentRecord, ConsentState, Message, MessageDirection, MessageEncoding, MessageKind, MessageState, MessageTemplate, MessageThread,
  MessagingCapability, MessagingReasonCode, NotificationPreferences, RenderedTemplate, ThreadKind,
} from '../../lib/api.ts';

export const THREAD_KINDS = ['customer', 'system'] as const satisfies readonly ThreadKind[];
export const CONSENT_STATES = ['unknown', 'pending_confirmation', 'opted_in', 'opted_out'] as const satisfies readonly ConsentState[];
export const DIRECTIONS = ['inbound', 'outbound'] as const satisfies readonly MessageDirection[];
export const MESSAGE_KINDS = ['conversation', 'system_notification', 'keyword_auto_reply'] as const satisfies readonly MessageKind[];
export const MESSAGE_STATES = ['queued', 'submitted', 'sent', 'delivered', 'failed', 'received', 'suppressed'] as const satisfies readonly MessageState[];
export const ENCODINGS = ['gsm7', 'ucs2'] as const satisfies readonly MessageEncoding[];
export const REASON_CODES = ['messaging_disabled', 'campaign_not_approved', 'number_not_assigned', 'no_messaging_permission'] as const satisfies readonly MessagingReasonCode[];

/** Permissions from the session (contract). */
export const READ_PERMISSIONS = ['messages:read_own', 'messages:read_all'] as const;
export const SEND_PERMISSION = 'messages:send';
export const TEMPLATES_MANAGE_PERMISSION = 'messages:templates_manage';
export const NOTIFICATIONS_PERMISSION = 'notifications:manage_own';

/** Server rule: a body is at most 1600 characters. Counted in UTF-16 units, never more lenient than the server. */
export const MAX_BODY_LENGTH = 1600;
/** Most messages rendered at once in a thread; earlier loaded ones sit behind a "show earlier" button. */
export const MESSAGE_WINDOW = 200;
/** Most rows read from one response; the rest are counted as skipped. */
export const MAX_ROWS = 500;

// ---- Small readers ------------------------------------------------------------------------------

const ID = /^[A-Za-z0-9_.:-]{1,128}$/;
const CODE = /^[A-Za-z0-9][A-Za-z0-9_.:/-]{0,79}$/;
const E164 = /^\+[1-9]\d{6,14}$/;
const VARIABLE = /^[A-Za-z_][A-Za-z0-9_]{0,39}$/;

export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
function oneOf<T extends string>(list: readonly T[], value: unknown): T | null {
  return typeof value === 'string' && (list as readonly string[]).includes(value) ? (value as T) : null;
}
function id(value: unknown): string | null {
  return typeof value === 'string' && ID.test(value) ? value : null;
}
function code(value: unknown): string | null {
  return typeof value === 'string' && CODE.test(value) ? value : null;
}
function time(value: unknown): string | null {
  return typeof value === 'string' && !Number.isNaN(Date.parse(value)) ? value : null;
}
function text(value: unknown, max: number): string {
  if (typeof value !== 'string') return '';
  // Message bodies keep their line breaks; other control characters are dropped.
  const clean = value.replace(/\r\n?/g, '\n').replace(/[\u0000-\u0009\u000b-\u001f\u007f]/g, '');
  return clean.length > max ? `${clean.slice(0, max - 1)}…` : clean;
}
function count(value: unknown): number {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0 ? value : 0;
}

/** `{data: X}` or a bare X: the contract wraps single resources, older routes sometimes don't. */
export function unwrap(value: unknown): unknown {
  return isRecord(value) && 'data' in value ? value.data : value;
}

/** The list cursor from `meta.next_cursor` (messaging contract) or `page.next_cursor` (older lists). */
export function nextCursorOf(value: unknown): string | null {
  if (!isRecord(value)) return null;
  const meta = isRecord(value.meta) ? value.meta.next_cursor : undefined;
  const page = isRecord(value.page) ? value.page.next_cursor : undefined;
  const cursor = meta ?? page;
  return typeof cursor === 'string' && cursor.length > 0 && cursor.length <= 1000 ? cursor : null;
}

/**
 * Defence in depth for carrier reasons (the server is the redactor): masks phone numbers, addresses
 * and long token-like strings and bounds the length.
 */
export function redactReason(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const clean = value.replace(/[\u0000-\u001f\u007f]+/g, ' ').trim();
  if (!clean) return null;
  const masked = clean
    .replace(/\+?\d[\d\s().-]{8,}\d/g, '[number]')
    .replace(/\b(?:\d{1,3}\.){3}\d{1,3}(?::\d+)?\b/g, '[address]')
    .replace(/\b(bearer|token|password|authorization|cookie|api[_-]?key)\b\s*[:=]?\s*\S+/gi, '$1 [removed]')
    .replace(/[A-Za-z0-9+/_=-]{32,}/g, '[removed]');
  return masked.length > 300 ? `${masked.slice(0, 299)}…` : masked;
}

export function readThread(value: unknown): MessageThread | null {
  if (!isRecord(value)) return null;
  const threadId = id(value.id);
  const numberId = id(value.number_id);
  const remote = typeof value.remote_e164 === 'string' && E164.test(value.remote_e164) ? value.remote_e164 : null;
  const kind = oneOf(THREAD_KINDS, value.kind);
  if (!threadId || !numberId || !remote || !kind) return null;
  return {
    id: threadId,
    number_id: numberId,
    remote_e164: remote,
    contact_id: id(value.contact_id),
    kind,
    // An unrecognised consent value is never shown as consent: it reads as "unknown".
    consent_state: oneOf(CONSENT_STATES, value.consent_state) ?? 'unknown',
    last_message_at: time(value.last_message_at),
    last_message_preview: text(value.last_message_preview, 120).replace(/\s+/g, ' ').trim(),
    unread_count: count(value.unread_count),
  };
}

export function readMessage(value: unknown): Message | null {
  if (!isRecord(value)) return null;
  const messageId = id(value.id);
  const threadId = id(value.thread_id);
  const direction = oneOf(DIRECTIONS, value.direction);
  const kind = oneOf(MESSAGE_KINDS, value.kind);
  const state = oneOf(MESSAGE_STATES, value.state);
  const created = time(value.created_at);
  if (!messageId || !threadId || !direction || !kind || !state || !created) return null;
  return {
    id: messageId,
    thread_id: threadId,
    direction,
    kind,
    body: text(value.body, MAX_BODY_LENGTH),
    segments: count(value.segments),
    encoding: oneOf(ENCODINGS, value.encoding) ?? 'gsm7',
    state,
    error_code: code(value.error_code),
    error_detail: redactReason(value.error_detail),
    actor_user_id: id(value.actor_user_id),
    template_id: id(value.template_id),
    created_at: created,
    submitted_at: time(value.submitted_at),
    delivered_at: time(value.delivered_at),
    provider_message_id: id(value.provider_message_id),
  };
}

export function readRows<T>(value: unknown, reader: (row: unknown) => T | null, max = MAX_ROWS): { rows: T[]; skipped: number; nextCursor: string | null } | null {
  const data = isRecord(value) ? value.data : null;
  if (!Array.isArray(data)) return null;
  const rows: T[] = [];
  let skipped = 0;
  for (const raw of data) {
    const row = rows.length < max ? reader(raw) : null;
    if (row) rows.push(row);
    else skipped += 1;
  }
  return { rows, skipped, nextCursor: nextCursorOf(value) };
}

export function readConsent(value: unknown): ConsentRecord | null {
  const v = unwrap(value);
  if (!isRecord(v)) return null;
  const state = oneOf(CONSENT_STATES, v.state);
  if (!state) return null;
  return {
    state,
    source: code(v.source),
    disclosure_version: typeof v.disclosure_version === 'string' && v.disclosure_version.length <= 100 ? v.disclosure_version : null,
    updated_at: time(v.updated_at),
  };
}

export function readTemplate(value: unknown): MessageTemplate | null {
  if (!isRecord(value)) return null;
  const templateId = id(value.id);
  const name = text(value.name, 100).trim();
  if (!templateId || !name || typeof value.body !== 'string') return null;
  const variables = Array.isArray(value.variables) ? [...new Set(value.variables.filter((v): v is string => typeof v === 'string' && VARIABLE.test(v)))].slice(0, 20) : templateVariables(value.body);
  return { id: templateId, name, body: text(value.body, MAX_BODY_LENGTH), variables, created_by: id(value.created_by), updated_at: time(value.updated_at) ?? '' };
}

export function readRendered(value: unknown): RenderedTemplate | null {
  const v = unwrap(value);
  if (!isRecord(v) || typeof v.body !== 'string') return null;
  const encoding = oneOf(ENCODINGS, v.encoding);
  if (!encoding) return null;
  return { body: text(v.body, MAX_BODY_LENGTH), segments: count(v.segments), encoding };
}

/** `{{name}}` placeholders in template order, without duplicates. */
export function templateVariables(body: string): string[] {
  const names: string[] = [];
  for (const match of body.matchAll(/\{\{\s*([A-Za-z_][A-Za-z0-9_]{0,39})\s*\}\}/g)) {
    const name = match[1];
    if (name && !names.includes(name)) names.push(name);
  }
  return names;
}

/** Variables still empty; the render request isn't sent until every one has a value. */
export function missingVariables(template: Pick<MessageTemplate, 'variables'>, values: Readonly<Record<string, string>>): string[] {
  return template.variables.filter(name => !(values[name] ?? '').trim());
}

/** `first_name` reads as "First name" in the form. */
export function variableLabel(name: string): string {
  const words = name.replace(/_+/g, ' ').trim();
  return words ? words[0]!.toUpperCase() + words.slice(1) : name;
}

// ---- Capability ---------------------------------------------------------------------------------

export type MessagingGate =
  | { state: 'enabled'; detail: string }
  | { state: 'pending_registration'; detail: string; reasonCode: MessagingReasonCode | null }
  | { state: 'disabled'; detail: string; reasonCode: MessagingReasonCode | null }
  | { state: 'not_allowed'; detail: string; reasonCode: MessagingReasonCode | null }
  | { state: 'unavailable'; detail: string; reasonCode: string | null }
  | { state: 'unknown'; detail: string };

/** Plain fallbacks used only when the server sends no detail sentence. */
export const REASON_FALLBACK: Record<MessagingReasonCode, string> = {
  campaign_not_approved: 'Texting starts after the carriers approve the 10DLC campaign.',
  number_not_assigned: 'This line isn’t assigned to the approved texting campaign yet.',
  messaging_disabled: 'Texting is turned off for this workspace.',
  no_messaging_permission: 'Your role doesn’t include texting. Ask an admin if you need it.',
};

/**
 * Reads `messaging.sms` (or `.mms`) from the capability lookup. Missing or unreadable means
 * unknown, never on. `pending_registration` is the status until the carriers approve the campaign.
 */
export function readMessagingCapability(value: unknown): MessagingCapability | null {
  if (!isRecord(value)) return null;
  const status = value.status;
  if (typeof status !== 'string' || typeof value.allowed !== 'boolean') return null;
  const reason = oneOf(REASON_CODES, value.reason_code);
  const detail = typeof value.detail === 'string' ? value.detail.replace(/\s+/g, ' ').trim().slice(0, 500) : '';
  // Statuses from before the SMS contract (unimplemented, unconfigured) are kept so the gate can explain them.
  return { status: status as MessagingCapability['status'], allowed: value.allowed, reason_code: reason, detail };
}

export function messagingGate(value: unknown): MessagingGate {
  const cap = readMessagingCapability(value);
  if (!cap) return { state: 'unknown', detail: 'Texting status couldn’t be read. Reload the page to check again.' };
  const reason = cap.reason_code;
  const detail = cap.detail || (reason ? REASON_FALLBACK[reason] : '');
  if (!cap.allowed) return { state: 'not_allowed', detail: detail || REASON_FALLBACK.no_messaging_permission, reasonCode: reason ?? 'no_messaging_permission' };
  switch (cap.status) {
    case 'enabled':
      return { state: 'enabled', detail };
    case 'pending_registration':
      return { state: 'pending_registration', detail: detail || REASON_FALLBACK.campaign_not_approved, reasonCode: reason };
    case 'disabled':
      return { state: 'disabled', detail: detail || REASON_FALLBACK.messaging_disabled, reasonCode: reason };
    default: {
      const raw = isRecord(value) && typeof value.reason_code === 'string' ? code(value.reason_code) : null;
      return { state: 'unavailable', detail: detail || 'Texting isn’t available in this version.', reasonCode: raw };
    }
  }
}

export function gateTitle(gate: MessagingGate): string {
  switch (gate.state) {
    case 'enabled':
      return 'Texting is available';
    case 'pending_registration':
      return 'Texting is waiting for carrier approval';
    case 'disabled':
      return 'Texting is turned off';
    case 'not_allowed':
      return 'Your role can’t send texts';
    case 'unavailable':
      return 'Texting isn’t available in this version';
    default:
      return 'Texting status is unknown';
  }
}

// ---- Consent ------------------------------------------------------------------------------------

export const CONSENT_LABEL: Record<ConsentState, string> = {
  opted_in: 'Opted in',
  pending_confirmation: 'Awaiting YES confirmation',
  opted_out: 'Opted out',
  unknown: 'No SMS consent on record',
};

export const CONSENT_HINT: Record<ConsentState, string> = {
  opted_in: 'They agreed to texts and confirmed by replying YES.',
  pending_confirmation: 'They asked for texts but haven’t replied YES yet. Texts start only after they confirm.',
  opted_out: 'They replied STOP. Nothing is sent to this number until they reply START or YES.',
  unknown: 'There’s no record of this number agreeing to texts.',
};

export type SendBlock = { blocked: false } | { blocked: true; code: string; reason: string };

/**
 * Whether Send is available, and the exact reason when it isn't. Order: capability (its detail is
 * shown verbatim), permission, thread kind, then consent. A new conversation (`first`) also needs
 * consent on record: the Brandcore SMS program only texts people who opted in and replied YES.
 */
export function sendBlock(input: {
  gate: MessagingGate;
  canSend: boolean;
  kind?: ThreadKind;
  consent: ConsentState | null;
  first?: boolean;
}): SendBlock {
  const { gate } = input;
  if (gate.state !== 'enabled') return { blocked: true, code: gate.state === 'unknown' ? 'capability_unknown' : ('reasonCode' in gate && gate.reasonCode) || gate.state, reason: gate.detail };
  if (!input.canSend) return { blocked: true, code: 'no_messaging_permission', reason: REASON_FALLBACK.no_messaging_permission };
  if (input.kind === 'system') return { blocked: true, code: 'system_thread', reason: 'These are automatic alerts to your own phone. Replies can’t be sent from here.' };
  switch (input.consent) {
    case 'opted_out':
      return { blocked: true, code: 'consent_opted_out', reason: 'This number opted out by replying STOP. Nothing can be sent until they reply START or YES.' };
    case 'pending_confirmation':
      return { blocked: true, code: 'consent_pending', reason: 'Awaiting YES confirmation. Texts can be sent once they reply YES.' };
    case 'unknown':
      return input.first ? { blocked: true, code: 'consent_unknown', reason: 'There’s no SMS consent on record for this number. Texts only go to people who opted in on brandcore.io and replied YES.' } : { blocked: false };
    case null:
      return input.first ? { blocked: true, code: 'consent_unchecked', reason: 'Check this number’s SMS consent first.' } : { blocked: false };
    default:
      return { blocked: false };
  }
}

// ---- Segments -----------------------------------------------------------------------------------

// GSM 03.38 default alphabet and its extension table (each extension character costs two septets).
const GSM_BASIC = new Set(
  '@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !"#¤%&\'()*+,-./0123456789:;<=>?¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà',
);
const GSM_EXTENDED = new Set('\f^{}\\[~]|€');

export interface SegmentInfo {
  encoding: MessageEncoding;
  /** Septets for GSM-7, UTF-16 code units for UCS-2. */
  units: number;
  segments: number;
  /** Units one part holds at this length: 160/153 (GSM-7) or 70/67 (UCS-2). */
  perSegment: number;
  /** Units left before the next part starts. */
  remaining: number;
  /** UTF-16 length, the unit of the 1600 limit. */
  length: number;
  overLimit: boolean;
  /** Up to three distinct characters that forced UCS-2. */
  nonGsm: string[];
}

/** Splits into parts the way carriers do: an escape pair or a surrogate pair is never split. */
function partsFor(costs: readonly number[], single: number, multi: number): number {
  const total = costs.reduce((a, b) => a + b, 0);
  if (total === 0) return 0;
  if (total <= single) return 1;
  let parts = 1;
  let used = 0;
  for (const cost of costs) {
    if (used + cost > multi) {
      parts += 1;
      used = 0;
    }
    used += cost;
  }
  return parts;
}

export function segmentInfo(body: string): SegmentInfo {
  const chars = [...body];
  const nonGsm: string[] = [];
  for (const ch of chars) {
    if (!GSM_BASIC.has(ch) && !GSM_EXTENDED.has(ch) && !nonGsm.includes(ch)) nonGsm.push(ch);
  }
  const gsm = nonGsm.length === 0;
  const costs = chars.map(ch => (gsm ? (GSM_EXTENDED.has(ch) ? 2 : 1) : ch.length));
  const units = costs.reduce((a, b) => a + b, 0);
  const single = gsm ? 160 : 70;
  const multi = gsm ? 153 : 67;
  const segments = partsFor(costs, single, multi);
  const perSegment = segments > 1 ? multi : single;
  const remaining = segments === 0 ? single : segments === 1 ? single - units : multi * segments - units;
  return { encoding: gsm ? 'gsm7' : 'ucs2', units, segments, perSegment, remaining: Math.max(0, remaining), length: body.length, overLimit: body.length > MAX_BODY_LENGTH, nonGsm: nonGsm.slice(0, 3) };
}

export const ENCODING_LABEL: Record<MessageEncoding, string> = { gsm7: 'GSM-7', ucs2: 'UCS-2' };

/** "2 parts, GSM-7" / "1 part, UCS-2". */
export function segmentLabel(info: Pick<SegmentInfo, 'segments' | 'encoding'>): string {
  return `${info.segments} part${info.segments === 1 ? '' : 's'}, ${ENCODING_LABEL[info.encoding]}`;
}

export type BodyProblem = 'empty' | 'too_long' | 'control_characters';

/** Client-side checks mirroring the server's 422 `invalid_body`; the server stays authoritative. */
export function bodyProblem(body: string): BodyProblem | null {
  if (!body.trim()) return 'empty';
  if (body.length > MAX_BODY_LENGTH) return 'too_long';
  if (/[\u0000-\u0009\u000b-\u001f\u007f]/.test(body.replace(/\r\n/g, '\n'))) return 'control_characters';
  return null;
}

export const BODY_PROBLEM_TEXT: Record<BodyProblem, string> = {
  empty: 'Write a message first.',
  too_long: `Messages can be at most ${MAX_BODY_LENGTH} characters.`,
  control_characters: 'Remove the hidden control characters (for example tabs) before sending.',
};

// ---- Delivery state and kinds --------------------------------------------------------------------

export const STATE_LABEL: Record<MessageState, string> = {
  queued: 'Queued',
  submitted: 'Submitted',
  sent: 'Sent',
  delivered: 'Delivered',
  failed: 'Failed',
  received: 'Received',
  suppressed: 'Not sent: this number opted out',
};

export const STATE_HINT: Record<MessageState, string> = {
  queued: 'Waiting to be handed to the carrier.',
  submitted: 'Handed to the carrier. Waiting for the carrier to accept it.',
  sent: 'The carrier accepted it. Delivery to the phone isn’t confirmed yet.',
  delivered: 'The carrier reported it delivered to the phone.',
  failed: 'The carrier couldn’t deliver it.',
  received: 'Received from this number.',
  suppressed: 'Nothing was sent because this number opted out.',
};

export const KIND_LABEL: Record<MessageKind, string | null> = {
  conversation: null,
  keyword_auto_reply: 'Automatic reply',
  system_notification: 'System notification',
};

export const THREAD_KIND_LABEL: Record<ThreadKind, string> = { customer: 'Customer', system: 'Your SMS alerts' };

// ---- Program keywords (docs/research/10dlc-registration.md, campaign payload) ---------------------

export interface KeywordRule {
  keywords: string[];
  effect: string;
  reply: string;
}

/** The registered keywords and the exact automatic replies (straight apostrophes, as sent). The server sends them, never the browser. */
export const KEYWORD_RULES: readonly KeywordRule[] = [
  {
    keywords: ['STOP', 'STOPALL', 'UNSUBSCRIBE', 'CANCEL', 'END', 'QUIT'],
    effect: 'Opts the number out. Nothing else is sent to it.',
    reply: "Brandcore: You're opted out of Brandcore texts. No further messages will be sent unless you opt in again.",
  },
  {
    keywords: ['HELP', 'INFO'],
    effect: 'Sends the help reply. Consent doesn’t change.',
    reply: 'Brandcore texts (customer service, project updates, system alerts): for help email info@brandcore.io. Msg frequency varies. Msg & data rates may apply. Reply STOP to opt out.',
  },
  {
    keywords: ['YES', 'START', 'UNSTOP'],
    effect: 'Confirms or restores consent.',
    reply: "Brandcore: You're confirmed for Brandcore texts (customer service, project updates or system alerts). Msg frequency varies. Msg & data rates may apply. Reply HELP for help, STOP to opt out.",
  },
];

// ---- Send errors --------------------------------------------------------------------------------

export function sendErrorText(error: { code: string; status?: number; outcomeUnknown?: boolean } | null, gate: MessagingGate): string {
  if (!error) return '';
  if (error.outcomeUnknown || error.code === 'network_error') return 'The server didn’t answer, so the message may or may not be queued. Press Send again: it won’t be sent twice.';
  if ((REASON_CODES as readonly string[]).includes(error.code)) {
    // The capability changed under us: prefer the server's current sentence when it's for the same reason.
    const reason = error.code as MessagingReasonCode;
    return 'reasonCode' in gate && gate.reasonCode === reason && gate.detail ? gate.detail : REASON_FALLBACK[reason];
  }
  switch (error.code) {
    case 'consent_opted_out':
      return 'Not sent: this number opted out.';
    case 'invalid_body':
      return 'The message can’t be sent as written. Check its length and remove unusual characters.';
    case 'invalid_e164':
      return 'That number isn’t a valid mobile number.';
    case 'permission_denied':
      return REASON_FALLBACK.no_messaging_permission;
    case 'idempotency_conflict':
      return 'That send was already used for a different message. Press Send again.';
    case 'rate_limited':
      return 'Too many messages at once. Wait a moment, then try again.';
    default:
      return error.status && error.status >= 500 ? 'The server couldn’t queue the message. Try again.' : 'The message couldn’t be sent.';
  }
}

// ---- Lists, merging and keyboard ----------------------------------------------------------------

/** Newest activity first; threads without activity last, then by id for a stable order. */
export function sortThreads(threads: readonly MessageThread[]): MessageThread[] {
  return [...threads].sort((a, b) => (b.last_message_at ?? '').localeCompare(a.last_message_at ?? '') || a.id.localeCompare(b.id));
}

export function mergeThreads(current: readonly MessageThread[], incoming: readonly MessageThread[]): MessageThread[] {
  const map = new Map(current.map(t => [t.id, t]));
  for (const t of incoming) map.set(t.id, t);
  return sortThreads([...map.values()]);
}

/** Oldest first, as a conversation reads. */
export function mergeMessages(current: readonly Message[], incoming: readonly Message[]): Message[] {
  const map = new Map(current.map(m => [m.id, m]));
  for (const m of incoming) map.set(m.id, m);
  return [...map.values()].sort((a, b) => a.created_at.localeCompare(b.created_at) || a.id.localeCompare(b.id));
}

/** The visible slice of a long thread: the newest MESSAGE_WINDOW unless expanded. */
export function messageWindow(total: number, expanded: boolean, size = MESSAGE_WINDOW): { from: number; hidden: number } {
  if (expanded || total <= size) return { from: 0, hidden: 0 };
  return { from: total - size, hidden: total - size };
}

/** Roving focus in the thread list: arrows move one row, Home and End jump. */
export function moveFocus(key: string, index: number, total: number): number | null {
  if (total <= 0) return null;
  switch (key) {
    case 'ArrowDown':
      return Math.min(index + 1, total - 1);
    case 'ArrowUp':
      return Math.max(index - 1, 0);
    case 'Home':
      return 0;
    case 'End':
      return total - 1;
    default:
      return null;
  }
}

/** Composer keys: Ctrl+Enter or Cmd+Enter sends; Enter alone is a new line. */
export function composerKey(event: { key: string; ctrlKey: boolean; metaKey: boolean; shiftKey: boolean; altKey: boolean; isComposing?: boolean }): 'send' | null {
  if (event.isComposing) return null;
  return event.key === 'Enter' && (event.ctrlKey || event.metaKey) && !event.altKey ? 'send' : null;
}

/** A fresh key per send attempt; a retry after an unknown outcome reuses it. */
export function newIdempotencyKey(prefix = 'msg'): string {
  const uuid = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
  return `${prefix}-${uuid}`;
}

// ---- Notification preferences (Settings > Notifications) ---------------------------------------

export function readPreferences(value: unknown): NotificationPreferences | null {
  const v = unwrap(value);
  if (!isRecord(v) || typeof v.sms_enabled !== 'boolean') return null;
  const c = isRecord(v.categories) ? v.categories : {};
  const q = isRecord(v.quiet_hours) ? v.quiet_hours : null;
  const hhmm = (x: unknown) => (typeof x === 'string' && /^([01]\d|2[0-3]):[0-5]\d$/.test(x) ? x : null);
  const quietStart = q ? hhmm(q.start) : null;
  const quietEnd = q ? hhmm(q.end) : null;
  const tz = q && typeof q.timezone === 'string' && /^[A-Za-z_+-]+(\/[A-Za-z0-9_+-]+){0,2}$/.test(q.timezone) ? q.timezone : null;
  return {
    sms_enabled: v.sms_enabled,
    sms_e164: typeof v.sms_e164 === 'string' && E164.test(v.sms_e164) ? v.sms_e164 : null,
    sms_verified_at: time(v.sms_verified_at),
    categories: { call_missed: c.call_missed === true, voicemail: c.voicemail === true, system_alerts: c.system_alerts === true },
    quiet_hours: quietStart && quietEnd && tz ? { start: quietStart, end: quietEnd, timezone: tz } : null,
    disclosure_version: typeof v.disclosure_version === 'string' && v.disclosure_version.length <= 100 ? v.disclosure_version : null,
  };
}
